<template>
    <div class="video-player">
        <video
            :src="streamUrl"
            ref="video"
        />
        <div class="video-player__controls">
            <i
                :class="['icon', isVideoPlaying ? 'icon-video_pause': 'icon-video_play']"
                @click="togglePlayback"
            />
            <i
                :class="['icon', isSoundOn ? 'icon-audio_stream' : 'icon-video_sound_mute_outline']"
                @click="$emit('toggleSound')"
            />
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import { mapActions } from 'vuex';
    import Logger from '@core/utils/loggers/Logger';
    import { loadScript } from '@app/utils/scriptLoader';

    export default Vue.component('VideoPlayer', {
        name: 'VideoPlayer',
        props: {
            streamUrl: {
                type: String,
                required: true,
            },
            videoType: {
                type: String,
                required: true,
            },
            rerenderingIndicator: {
                type: [String, Number],
                default: '',
            },
            isSoundOn: {
                type: Boolean,
                required: true,
            }
        },
        data() {
            return {
                isVideoPlaying: false,
            };
        },
        async mounted() {
            if (!this.checkCompatibility()) {
                await this.loadRequirements();
                this.videoWrapper = await window.prepareVideo(this.$refs.video, this.streamUrl);
                this.initializeVideoPlayer();
            } else {
                this.initializeVideoPlayer();
            }
        },
        watch: {
            rerenderingIndicator(newValue, oldValue) {
                if (newValue !== oldValue) {
                    const video = this.$refs.video;
                    const isPlaying = video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2;
                    if (!isPlaying && this.isVideoPlaying) {
                        this.playVideo();
                    }
                }
            },
            streamUrl() {
                this.initializeVideoPlayer();
            },
            isSoundOn() {
                this.setVolume();
            }
        },
        methods: {
            ...mapActions('errors', [
                'showError',
            ]),
            checkCompatibility() {
                const video = this.$refs.video;
                return video.canPlayType(this.videoType);
            },
            async loadRequirements() {
                const hlsUrl = `${window.location.origin}/static/js/hls-0_12_4.js`;
                const preparatorUrl = `${window.location.origin}/static/js/prepareVideo-1_0_0.js`;

                await loadScript(hlsUrl, 'hls',);
                await loadScript(preparatorUrl, 'videoPreparator');
            },
            async playVideo() {
                const video = this.$refs.video;
                if (!video) {
                    return;
                }
                try {
                    await video.play();
                    this.isVideoPlaying = true;
                    if (this.videoWrapper) {
                        this.videoWrapper.startLoad();
                    }
                } catch (e) {
                    Logger.error(e);
                }
            },
            pauseVideo() {
                if (this.isVideoPlaying === false) {
                    return;
                }
                const video = this.$refs.video;
                if (!video) {
                    return;
                }
                video.pause();
                this.isVideoPlaying = false;
                if (this.videoWrapper) {
                    this.videoWrapper.stopLoad();
                }
            },
            togglePlayback() {
                if (this.isVideoPlaying) {
                    this.pauseVideo();
                } else {
                    this.playVideo();
                }
            },
            setVolume() {
                this.$refs.video.volume = this.isSoundOn ? 1 : 0;
            },
            initializeVideoPlayer() {
                this.playVideo();
                this.setVolume();
            },
        },
        beforeDestroy() {
            if (this.videoWrapper) {
                this.videoWrapper.stopLoad();
            }
        }
    });
</script>



// WEBPACK FOOTER //
// src/app/modules/shared/components/VideoPlayer.vue